import { zodResolver } from '@hookform/resolvers/zod';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { addDays, parseISO } from 'date-fns';
import { useContext, useEffect, useMemo, useRef } from 'react';
import type { DefaultValues } from 'react-hook-form';
import { useForm, useFormContext, useWatch } from 'react-hook-form';
import useSWR, { useSWRConfig } from 'swr';
import { z } from 'zod';

import { UserContext } from '~/auth/userContext';
import { revalidateComplianceScreen } from '~/components/compliance/revalidateComplianceScreen';
import { dateToDateString } from '~/components/utils/dateUtils';
import { propertiesFormList } from '~/data/generated/endpoints/properties/properties';
import {
  violationsCreate,
  violationsPartialUpdate,
} from '~/data/generated/endpoints/violations/violations';
import type { PropertyForm } from '~/data/generated/model';
import { VALID_DATE_YEAR_MESSAGE, validateDateYear } from '~/data/schemas/zodHelpers';
import useLAISnackbar, { ActionEnum } from '~/hooks/useLAISnackbar';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { usePropertyCars } from '~/hooks/usePropertyCars';

import type { FieldDef } from '../form/utils';
import { createFormInvalidHandler, FieldAndCell } from '../form/utils';
import type { LAIUseFormReturn, LiftAIFormProps } from './SelectDocumentTypeDialog';

// `code` is a field-level constraint (not a superRefine) so its error still
// surfaces even when another required field — `dateOpen` in particular — is
// completely empty. A missing required field makes Zod treat the whole
// object parse as aborted, which skips every superRefine-based effect
// chained onto it; a field-level validator runs independently of that and
// isn't affected.
const makeViolationFormBaseSchema = (isUK: boolean) =>
  z.object({
    carId: z.string(),
    // Required for US, but LOLER (UK) defects carry no code and the backend
    // accepts blank — mirrors the same carve-out on the creation form
    // (inspectionReportForm.tsx) (AMP-686).
    code: isUK ? z.string() : z.string().min(1, { message: 'Violation code is required' }),
    location: z.string().nullable(),
    // Emptiness is checked in the superRefine below, since whether it is
    // allowed depends on severity. The year check still applies to anything
    // entered. Mirrors inspectionReportForm.tsx's violationSchema (AMP-697).
    correctionDue: z.string().refine((value) => !value || validateDateYear(value), {
      message: VALID_DATE_YEAR_MESSAGE,
    }),
    dateOpen: z
      .string()
      .refine(validateDateYear, { message: VALID_DATE_YEAR_MESSAGE })
      .refine((date) => new Date(date) <= new Date(), {
        message: 'Open date cannot be in the future',
      }),
    responsibility: z.enum(['owner', 'county', 'service_provider']).nullable(),
    description: z.string().min(10, { message: 'Description must be at least 10 characters' }),
    // LOLER (UK) per-defect category. A = urgent, B = standard, C =
    // observation/recommendation (no deadline). Shown only to en-GB users;
    // null for US. Mirrors inspectionReportForm.tsx's violationSchema
    // (AMP-697).
    severity: z.enum(['A', 'B', 'C']).nullable().optional(),
  });

const correctionDueDateRefinement = <
  T extends z.infer<ReturnType<typeof makeViolationFormBaseSchema>>,
>(
  data: T,
  ctx: z.RefinementCtx,
) => {
  if (
    data.dateOpen &&
    data.correctionDue &&
    new Date(data.correctionDue) < new Date(data.dateOpen)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Correction due date cannot be earlier than the open date',
      path: ['correctionDue'],
    });
  }
};

// A category C defect is an observation: it needs to be recorded, but carries
// no deadline and nobody chases it, so the due date is optional for C alone.
// Every other defect still requires one — including every US defect, which
// carries no severity at all, so this never relaxes the US form. The gate is
// the severity value, never the locale.
//
// Reads `data.severity` as entered/loaded, not locale-clamped: an earlier
// version of this schema clamped a non-UK payload's severity to null before
// this check, on the theory that a non-UK form should never let a stray
// severity value waive the requirement. That broke the real cross-locale
// edit path — locale here is the VIEWER's own group, not the defect's origin
// (app/routes/_app.tsx), so e.g. a LiftAI admin (en-US) editing one of TLC's
// (en-GB) category-C defects would get a spurious "Correction due date is
// required" error on a field the UI doesn't even mark as required, blocking
// the save outright. There is no live path today for a non-UK ADD flow to
// carry a non-null severity into this check (getDefaultViolationValues always
// sets null, and the field isn't rendered), so reading the raw value here is
// safe; the outgoing payload still omits severity for non-UK viewers (see
// ViolationForm's submit handler) so it can never reach the API, and
// Violation.clean() re-enforces this same rule server-side regardless.
// Mirrors inspectionReportForm.tsx (CX-416, AMP-697).
const correctionDueRequiredUnlessCategoryC = <
  T extends z.infer<ReturnType<typeof makeViolationFormBaseSchema>>,
>(
  data: T,
  ctx: z.RefinementCtx,
) => {
  if (data.severity !== 'C' && !data.correctionDue) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Correction due date is required',
      path: ['correctionDue'],
    });
  }
};

const makeViolationFormSchema = (isUK: boolean) =>
  makeViolationFormBaseSchema(isUK)
    .superRefine(correctionDueDateRefinement)
    .superRefine(correctionDueRequiredUnlessCategoryC);

// The form carries two keys the create/update request does not: `propertyId`
// (it picks the car list) and `isCarChangeable` (it gates the pickers). The
// resolver's field-values type has to cover every key the form holds, so this
// is the schema the form validates against. The submit handler re-parses with
// `makeViolationFormSchema`, which strips both back out of the payload.
const makeViolationFormValuesSchema = (isUK: boolean) =>
  makeViolationFormBaseSchema(isUK)
    .extend({
      // Optional, not required: this key is never rendered, so a caller seeding
      // `initialData` without it would fail validation on a field the user
      // cannot see or fix. That failure surfaces only as a toast here, since
      // this form has no `isHiddenField` predicate the way TicketForm does.
      // Absent means "not locked" — the pickers stay editable unless something
      // explicitly sets `false`.
      isCarChangeable: z.boolean().optional(),
      propertyId: z.string().optional(),
    })
    .superRefine(correctionDueDateRefinement)
    .superRefine(correctionDueRequiredUnlessCategoryC);

export type ViolationFormData = z.infer<ReturnType<typeof makeViolationFormValuesSchema>>;

const propertyOptionsFetcher = async (): Promise<{ properties: PropertyForm[] }> => {
  try {
    const { data: properties } = await propertiesFormList();
    return { properties };
  } catch (error) {
    console.error('Failed to fetch properties:', error);
    return { properties: [] };
  }
};

export const ViolationFields = ({ disabled }: { disabled?: boolean } = {}) => {
  const { control } = useFormContext<ViolationFormData>();
  const propertyId = useWatch({ control, name: 'propertyId' });
  const isCarChangeable = useWatch({ control, name: 'isCarChangeable' });
  const severity = useWatch({ control, name: 'severity' });
  const { locale } = useContext(UserContext);
  const isUK = locale === 'en-GB';
  const terms = useLocaleTerms();

  // Fetch properties
  const { data } = useSWR('ViolationForm.Options', propertyOptionsFetcher);

  // Use the usePropertyCars hook to get cars for selected property
  const { cars, loading: carsLoading } = usePropertyCars(propertyId ?? null);

  const formFields: FieldDef[] = useMemo(
    () =>
      [
        {
          name: 'propertyId',
          label: 'Property',
          type: 'autocomplete' as const,
          options:
            data?.properties?.map((property) => ({
              id: property.id,
              label: property.name,
            })) ?? [],
          loading: !data?.properties,
          required: true,
          disabled: isCarChangeable === false,
          gridProps: { xs: 12 },
        },
        {
          name: 'carId',
          label: terms.vehicle,
          type: 'autocomplete',
          options:
            cars?.map((car) => ({
              id: car.id,
              label: car.name + (car.ahjId ? ` - ${car.ahjId}` : ''),
            })) ?? [],
          loading: carsLoading,
          required: true,
          disabled: !propertyId || isCarChangeable === false,
          gridProps: { xs: 12 },
        },
        {
          name: 'code',
          label: `${terms.violation} Code`,
          type: 'text',
          // Required for US; LOLER (UK) defects have no codes. US emptiness is
          // enforced imperatively in ViolationForm's submit handler, mirroring
          // the creation form (AMP-686).
          required: !isUK,
          gridProps: { xs: 12 },
        },
        ...(isUK
          ? [
              {
                name: 'severity',
                label: 'Severity',
                type: 'select',
                required: false,
                options: [
                  { id: 'A', label: 'A (Urgent)' },
                  { id: 'B', label: 'B (Standard)' },
                  { id: 'C', label: 'C (Observation)' },
                ],
                gridProps: { xs: 12 },
              } as FieldDef,
            ]
          : []),
        {
          name: 'location',
          label: 'Location',
          type: 'text',
          required: false,
          gridProps: { xs: 12 },
        },
        {
          name: 'dateOpen',
          label: 'Date Open',
          type: 'date',
          required: true,
          gridProps: { xs: 6 },
        },
        {
          name: 'correctionDue',
          label: 'Correction Due',
          type: 'date',
          // A category C defect is an observation with no deadline, so the
          // field stops being required for C and only for C — the zod schema
          // enforces the same rule (correctionDueRequiredUnlessCategoryC).
          required: severity !== 'C',
          gridProps: { xs: 6 },
        },
        {
          name: 'responsibility',
          label: 'Responsibility',
          type: 'select',
          required: false,
          options: [
            { id: 'owner', label: 'Owner' },
            { id: 'service_provider', label: 'Service Provider' },
            { id: 'county', label: 'County' },
          ],
          gridProps: { xs: 12 },
        },
        {
          name: 'description',
          label: 'Description',
          type: 'text',
          required: true,
          gridProps: { xs: 12 },
        },
      ].map((f) => (disabled ? { ...f, disabled: true } : f)) as FieldDef[],
    [
      data?.properties,
      cars,
      carsLoading,
      isCarChangeable,
      propertyId,
      disabled,
      isUK,
      terms,
      severity,
    ],
  );

  return (
    <>
      <Typography variant="h6" my={2}>
        {terms.violation} Details
      </Typography>
      <Grid container spacing={2}>
        {formFields.map((field) => (
          <FieldAndCell key={field.name} field={field} control={control} />
        ))}
      </Grid>
    </>
  );
};

// `dateOpen` is deliberately absent. It is required, but its schema refines on
// `new Date(date) <= new Date()`, and `new Date('')` is an Invalid Date, so an
// empty-string default fails with "Open date cannot be in the future" instead of
// the required message. Leaving it undefined keeps the field blank and reports
// "This field is required", which is what the user needs to read.
export const getDefaultValues = (): DefaultValues<ViolationFormData> => ({
  isCarChangeable: true,
  propertyId: '',
  carId: '',
  code: '',
  correctionDue: '',
  location: '',
  description: '',
  responsibility: null,
  severity: null,
});

export const ViolationForm: React.FC<
  LiftAIFormProps<ViolationFormData> & { violationId?: string }
> = ({ children, onSubmit, initialData, violationId }) => {
  const { showEntityActionSnackbar, showSnackbar } = useLAISnackbar();
  const { locale } = useContext(UserContext);
  const terms = useLocaleTerms();
  const { handleSubmit, reset, control, getValues, setValue } = useFormContext<ViolationFormData>();
  // The cache-scoped mutate, not the module-level one exported from `swr`: the
  // global mutate is bound to the default cache and silently does nothing under
  // an `SWRConfig` that supplies its own provider.
  const { mutate } = useSWRConfig();

  // LOLER (UK) 90-day correction-due prefill on entering severity category B —
  // mirrors inspectionReportForm.tsx's ViolationSeverityField (AMP-697). B is
  // the ONLY category that prefills: A carries its own urgent timescale set by
  // hand, and C is a no-deadline observation, so prefilling either would put
  // the wrong items on the customer's chase list. Keep `severity === 'B'`
  // exact rather than "not A".
  //
  // Unlike the field-array precedent, this form has a single violation, not a
  // `violations.map()`, so there is no per-row component that mounts fresh
  // only after `reset(initialData)` has already applied — this component is
  // present from the first render. On that first render `watch('severity')`
  // still reads the pre-reset default; the reset effect below's `reset()`
  // call updates react-hook-form's internal state synchronously, but the
  // `watch` subscription only pushes that update out on a later render.
  // Reading `getValues('severity')` inside the effect body (instead of
  // closing over the `severity` watched below) sidesteps that lag entirely:
  // by the time this effect runs, the reset effect below has already applied
  // (effects run in declaration order within a component), so getValues()
  // already reflects it. `prevSeverityRef` is re-seeded from `initialData`
  // inside the reset effect below, not just at mount, so swapping in a
  // different document on an already-mounted form is also treated as
  // "already at its own severity" rather than a fresh transition into it —
  // no clobber.
  const prevSeverityRef = useRef<ViolationFormData['severity']>(initialData?.severity ?? null);

  useEffect(() => {
    if (!initialData) {
      return;
    }
    // Merge over the defaults: reset() replaces the whole values object, so an
    // omitted key would blank a field that has a non-empty default. See AMP-691.
    reset({
      ...getDefaultValues(),
      ...initialData,
    });
    prevSeverityRef.current = initialData?.severity ?? null;
  }, [initialData, reset]);

  const severity = useWatch({ control, name: 'severity' });

  useEffect(() => {
    const currentSeverity = getValues('severity');
    const enteredB = currentSeverity === 'B' && prevSeverityRef.current !== 'B';
    prevSeverityRef.current = currentSeverity;
    if (!enteredB) {
      return;
    }
    const dateOpen = getValues('dateOpen');
    if (!dateOpen) {
      return;
    }
    const parsed = parseISO(dateOpen);
    if (Number.isNaN(parsed.getTime())) {
      return;
    }
    setValue('correctionDue', dateToDateString(addDays(parsed, 90)), {
      shouldValidate: true,
      shouldDirty: true,
    });
    // `severity` (watched) is the trigger for re-running this effect; the
    // body always reads the current value via getValues() rather than that
    // closure, so it can't act on a stale pre-reset value. getValues/setValue
    // are react-hook-form-stable and listed only for lint completeness.
  }, [severity, getValues, setValue]);

  return (
    <form
      noValidate
      style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
      onSubmit={handleSubmit(
        async (data) => {
          try {
            const violationData = makeViolationFormSchema(locale === 'en-GB').parse(data);
            const isEdit = !!violationId;
            const isUK = locale === 'en-GB';

            // The form's parsed ViolationFormData maps onto the create/update
            // request body (camelCase payload unchanged), except:
            // - An empty correction-due reaches the API as null, not "" —
            //   only a category C can get here empty, and null is what "no
            //   deadline" means on the wire. Mirrors inspectionReportForm.tsx
            //   (CX-416).
            const payload: Omit<typeof violationData, 'correctionDue'> & {
              correctionDue: string | null;
            } = {
              ...violationData,
              correctionDue: violationData.correctionDue || null,
            };
            // - Severity is a LOLER (UK) concept: a non-UK viewer's form
            //   never renders the field, so `payload.severity` reflects the
            //   record's real value (loaded by getEditViolationValues on the
            //   edit path), not anything this viewer entered or can even see.
            //   Omit the key rather than sending an explicit null: severity
            //   is optional on ViolationCreateUpdateSerializer, so omission
            //   is a no-op on create and — critically — leaves the stored
            //   value alone on a PATCH, where an explicit null would
            //   overwrite it. Without this, a non-UK-locale viewer (e.g. a
            //   LiftAI admin) editing one of TLC's defects for an unrelated
            //   reason would silently wipe its category. (AMP-697)
            if (!isUK) {
              delete payload.severity;
            }

            const { data: violation } = isEdit
              ? await violationsPartialUpdate(violationId, payload)
              : await violationsCreate(payload);

            onSubmit(violation);
            // Adding or editing a defect moves every count on the compliance
            // screen, exactly as resolving one does, but this handler used to
            // invalidate only the panel's own defect list. The summary tiles,
            // the severity donut, the open-vs-closed and upcoming-due charts
            // and the grid row's open-defect count all sit on keys whose
            // background revalidation is deliberately off, so they kept
            // pre-change numbers until a full page reload. Entering a defect is
            // the more common action than resolving one, so this was the wider
            // half of the same bug. Invalidated from the mutation site rather
            // than from each dialog that hosts the form, so the compliance
            // panel, the upload-document flow and the document reviewer all get
            // it. Awaited so the form cannot report success over a half-applied
            // refresh. (CX-416)
            await revalidateComplianceScreen(mutate, violationData.carId);

            showEntityActionSnackbar(
              {
                name: terms.violation,
                action: isEdit ? ActionEnum.Update : ActionEnum.Create,
                id: violationData.carId,
              },
              {
                variant: 'success',
              },
            );
          } catch (err) {
            console.error('Violation form error:', err);
            showEntityActionSnackbar(
              {
                name: terms.violation,
                action: ActionEnum.Fail,
                id: data.carId,
              },
              {
                variant: 'error',
              },
            );
          }
        },
        createFormInvalidHandler<ViolationFormData>({ formName: 'ViolationForm', showSnackbar }),
      )}
    >
      {children}
    </form>
  );
};

function useViolationForm(): LAIUseFormReturn<ViolationFormData> {
  const { locale } = useContext(UserContext);
  const isUK = locale === 'en-GB';
  const resolverSchema = useMemo(() => makeViolationFormValuesSchema(isUK), [isUK]);

  const hookForm = useForm<ViolationFormData>({
    mode: 'all',
    resolver: zodResolver(resolverSchema),
    defaultValues: getDefaultValues(),
  });

  return {
    hookForm,
    Form: ViolationForm,
    Fields: ViolationFields,
  };
}

export { useViolationForm };
